import React from 'react';
import { TripListItem } from '../types';
import { resolveDisplayStatus } from '../utils';
import SceneCover from './SceneCover';
import StatusPill from './StatusPill';
import Flags from './Flags';
import MetaLine from './MetaLine';
import Chevron from './Chevron';
import KebabMenu, { KebabIcons } from './KebabMenu';

// A whole-card-tappable trip. SceneCover + (StatusPill + Flags top-left) +
// KebabMenu (top-right) + (Title + MetaLine + Chevron). Status
// is derived client-side from the raw dates. See design-handoff.md C.1.

export interface TripCardProps {
  trip: TripListItem;
  onOpen: (trip: TripListItem) => void;
  /** Kebab → Share (parent opens the trip share dialog). */
  onShare: (trip: TripListItem) => void;
  /** Kebab → Rename (parent opens the name dialog). */
  onRename: (trip: TripListItem) => void;
  /** Kebab → Trip preferences (open the edit form). */
  onEdit: (trip: TripListItem) => void;
  /** Kebab → Create copy (routes to the pre-filled create form). */
  onCopy: (trip: TripListItem) => void;
  /** Kebab → Delete (parent confirms + deletes). */
  onDelete: (trip: TripListItem) => void;
}

const TripCard: React.FC<TripCardProps> = ({ trip, onOpen, onShare, onRename, onEdit, onCopy, onDelete }) => {
  const status = resolveDisplayStatus(trip.dateStart, trip.dateEnd, trip.stage);

  return (
    // Relative wrapper so the kebab can sit OUTSIDE the open-button (no nested
    // interactive) yet overlay the card's top-right corner.
    <div style={{ position: 'relative' }}>
      <button
        onClick={() => onOpen(trip)}
        style={{
          display: 'block',
          width: '100%',
          textAlign: 'left',
          background: '#ffffff',
          border: 'none',
          borderRadius: 22,
          overflow: 'hidden',
          boxShadow: '0 2px 10px rgba(20,40,30,0.06)',
          cursor: 'pointer',
          padding: 0,
        }}
      >
        <div style={{ position: 'relative', height: 120 }}>
          <SceneCover template={trip.cover.template} palette={trip.cover.palette} muted={status === 'past'} />
          {/* Badges cluster top-left (status + country flags); the kebab owns the
              top-right corner. */}
          <div style={{ position: 'absolute', top: 10, left: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
            <StatusPill status={status} />
            {trip.countrySet.length > 0 && (
              <div
                style={{
                  padding: '3px 8px',
                  borderRadius: 999,
                  background: 'rgba(255,255,255,0.82)',
                  backdropFilter: 'blur(4px)',
                  boxShadow: '0 1px 2px rgba(20,40,30,0.08)',
                }}
              >
                <Flags countries={trip.countrySet} />
              </div>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontFamily: 'Fraunces, Georgia, serif',
                fontSize: 18,
                fontWeight: 600,
                color: '#16241d',
                marginBottom: 4,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {trip.title}
            </div>
            <MetaLine dateStart={trip.dateStart} dateEnd={trip.dateEnd} alternativesCount={trip.alternativesCount} durationPreset={trip.durationPreset} />
          </div>
          <Chevron />
        </div>
      </button>
      <div style={{ position: 'absolute', top: 10, right: 10 }} onClick={(e) => e.stopPropagation()}>
        <KebabMenu
          ariaLabel="Trip options"
          items={[
            { label: 'See alternatives', icon: KebabIcons.layers, onSelect: () => onOpen(trip) },
            { label: 'Share', icon: KebabIcons.share, onSelect: () => onShare(trip) },
            { label: 'Rename', icon: KebabIcons.pencil, onSelect: () => onRename(trip) },
            { label: 'Trip preferences', icon: KebabIcons.sliders, onSelect: () => onEdit(trip) },
            { label: 'Create copy', icon: KebabIcons.copy, onSelect: () => onCopy(trip) },
            { label: 'Delete', icon: KebabIcons.trash, tone: 'danger', onSelect: () => onDelete(trip) },
          ]}
        />
      </div>
    </div>
  );
};

export default TripCard;
